import React, {
  ChangeEvent,
  SyntheticEvent,
  useCallback,
  useEffect,
  useState,
} from 'react';
import AutoCompletedInput from 'core/components/AutoComplete';
import LocationOnIcon from '@mui/icons-material/LocationOn';
import CloseIcon from '@mui/icons-material/Close';
import uniqueId from 'lodash/uniqueId';
import {
  AddressDTO,
} from 'dtos';
import isNil from 'lodash/isNil';
import {
  autosuggestHighlightParse,
} from '@populateui/utils';
import clsx from 'clsx';
import {
  useGetGooglePlaces,
  useGoogleMapGeoCode,
} from 'pages/Dashboard/pages/Charts/pages/Details/components/hooks';
import Spinner from 'pages/Dashboard/components/Spinner';
import debounce from 'lodash/debounce';

// Keep Address form compatible with existing `formatPlace()` + stored values
// by continuing to use the legacy AutocompletePrediction shape in forms/UI.
export type Place = Partial<google.maps.places.AutocompletePrediction> | null;

type Props = {
  onChange?: (value: AddressDTO, event?: ChangeEvent<HTMLInputElement>) => void;
  onResolvingChange?: (isResolving: boolean) => void;
  value?: Place;
  onBlur?: () => void;
  disabled?: boolean;
  className?: string;
};

export default function AddressSuggestion({
  onChange,
  onResolvingChange,
  value: externalValue,
  onBlur,
  disabled,
  className,
}: Props) {
  const [value, setValue] = useState<Place | undefined>(externalValue);
  const [address, setAddress] = useState<string | undefined>('');
  const [inputValue, setInputValue] = useState('');

  const {
    data: options,
    isFetching: isFetchingPlaces,
  } = useGetGooglePlaces(inputValue);

  const {
    data,
    error: geoCodeError,
    isFetching: isFetchingGeoCode,
  } = useGoogleMapGeoCode(address ?? '');
  const isLoading = isFetchingPlaces || isFetchingGeoCode;

  useEffect(() => {
    onResolvingChange?.(isFetchingGeoCode);
  }, [isFetchingGeoCode, onResolvingChange]);

  useEffect(() => {
    if (isNil(geoCodeError) && !isNil(data)) {
      onChange?.(data);
    }
  }, [data]);

  useEffect(() => {
    if (!isNil(externalValue)) {
      setValue(externalValue);
    }
  }, [externalValue]);

  const onInputChange = useCallback(debounce((
    event: SyntheticEvent,
    value: string,
  ) => {
    if (!isNil(event)) {
      setInputValue(value);
    }
  }, 700), []);

  return (
    <AutoCompletedInput<Place>
      id="address-suggestion"
      className={clsx('w-full !pointer-events-auto', className)}
      onBlur={onBlur}
      getLabel={(option) => (option?.description ?? '')}
      options={options ?? []}
      filterOptions={(x) => x}
      clearOnBlur={false}
      loading={isFetchingPlaces}
      loadingText="Loading..."
      value={value ?? null}
      filterSelectedOptions
      noOptionsText="No locations"
      placeholder="Enter Address"
      clearIcon={<CloseIcon className="!h-4 !w-4 -m-0.5" />}
      popupIcon={isLoading ? <Spinner /> : undefined}
      dropdownClassName={clsx('!pointer-events-auto', {
        '!p-2': (options ?? []).length === 0,
      })}
      onChange={(value) => {
        const newValue = value as Place;
        setValue(newValue);
        setAddress(newValue?.description);
      }}
      disabled={disabled}
      onInputChange={onInputChange}
      renderOption={(props, option) => {
        const matches = option?.structured_formatting?.main_text_matched_substrings ?? [];
        const parts = autosuggestHighlightParse(
          option?.structured_formatting?.main_text ?? '',
          matches.map((match) => [match.offset, match.offset + match.length]),
        );
        return (
          <li
            {...props}
            key={uniqueId('option_')}
            className={clsx(
              'flex items-center hover:bg-gray-100 !text-xs cursor-pointer',
              props.className,
            )}
          >
            <div className="mx-1">
              <LocationOnIcon className="text-gray-400 !text-base" />
            </div>
            <div className="w-[calc(100%-44px)]">
              {parts.map((part, index) => (
                <span
                  key={uniqueId(`part_${index}`)}
                  className={clsx({ 'font-semibold': part.highlight })}
                >
                  {part.text}
                </span>
              ))}
              <p className=" text-gray-400">
                {option?.structured_formatting?.secondary_text}
              </p>
            </div>
          </li>
        );
      }}
    />
  );
}
